/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, text, serial, integer, boolean, timestamp, jsonb } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Learning Activity Types - We have two types for this temporarily:

// ACTIVITY_TYPES is aligned with the types used for learning activities by the learning client

// Learning Activity Types
export const ACTIVITY_TYPES = {
  VIDEO: 'video',
  PDF: 'pdf',
  SLIDES: 'slides',
  IMAGE: 'image',
  AUDIO: 'audio',
  INTERACTIVE: 'interactive',
  TOOL: 'tool',
  QUIZ: 'quiz',
  ASSESSMENT: 'assessment',
  NARRATION: 'narration',
  PODCAST: 'podcast',
  CODE: 'code',
  MATH: 'math'
} as const;

export type ActivityType = typeof ACTIVITY_TYPES[keyof typeof ACTIVITY_TYPES];

// External Content Types
export const LEARNING_CONTENT_TYPES = {
  VIDEO: 'video',
  PDF: 'pdf',
  TEXTBOOK: 'textbook',
  ARTICLE: 'article',
  INTERACTIVE: 'interactive',
  COURSE: 'course',
  TUTORIAL: 'tutorial'
} as const;

export type LearningContentType = typeof LEARNING_CONTENT_TYPES[keyof typeof LEARNING_CONTENT_TYPES];

// Audience Levels for content
export const AUDIENCE_LEVELS = {
  BEGINNER: 'beginner',
  INTERMEDIATE: 'intermediate',
  ADVANCED: 'advanced',
  EXPERT: 'expert',
  ALL_LEVELS: 'all_levels'
} as const;

export type AudienceLevel = typeof AUDIENCE_LEVELS[keyof typeof AUDIENCE_LEVELS];

// Difficulty Levels for learning plans and activities
export const DIFFICULTY_LEVELS = {
  BEGINNER: 'beginner',
  INTERMEDIATE: 'intermediate',
  ADVANCED: 'advanced',
  EXPERT: 'expert'
} as const;

export type DifficultyLevel = typeof DIFFICULTY_LEVELS[keyof typeof DIFFICULTY_LEVELS];

// Reading Levels for learning plans 
export const READING_LEVELS = {
  GRADE_1: 'grade_1',
  GRADE_2: 'grade_2',
  GRADE_3: 'grade_3',
  GRADE_4: 'grade_4',
  GRADE_5: 'grade_5',
  GRADE_6: 'grade_6',
  GRADE_7: 'grade_7',
  GRADE_8: 'grade_8',
  GRADE_9: 'grade_9',
  GRADE_10: 'grade_10',
  GRADE_11: 'grade_11',
  GRADE_12: 'grade_12',
  UNDERGRAD: 'undergrad',
  GRADUATE: 'graduate',
  PHD: 'phd'
} as const;

export type ReadingLevel = typeof READING_LEVELS[keyof typeof READING_LEVELS];

// AI Assistance Levels
export const AI_ASSISTANCE_LEVELS = {
  MINIMAL: 'minimal',
  MODERATE: 'moderate',
  EXTENSIVE: 'extensive'
} as const;

export type AIAssistanceLevel = typeof AI_ASSISTANCE_LEVELS[keyof typeof AI_ASSISTANCE_LEVELS];

// Presentation Modalities
export const PRESENTATION_MODALITIES = {
  TEXT: 'text',
  VIDEO: 'video',
  AUDIO: 'audio',
  INTERACTIVE: 'interactive',
  MIXED: 'mixed'
} as const;

export type PresentationModality = typeof PRESENTATION_MODALITIES[keyof typeof PRESENTATION_MODALITIES];

// Learning Outcome Status
export const LEARNING_OUTCOME_STATUS = {
  INITIALIZED: 'initialized',
  APPROVED: 'approved', 
  GENERATING: 'generating',
  FINALIZED: 'finalized'
} as const;

export type LearningOutcomeStatus = typeof LEARNING_OUTCOME_STATUS[keyof typeof LEARNING_OUTCOME_STATUS];

// Learning Activities
export const learningActivities = pgTable("learning_activities", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  activityType: text("activity_type"), // video, pdf, slides, audio, tool, quiz, narration
  resourceId: integer("resource_id"), // content ID for resource type
  assessmentId: integer("assessment_id"), // assessment ID for assessment type
  toolUrl: text("tool_url"), // URL for tool type activities
  resourceUrl: text("resource_url"), // Direct URL to the resource (for convenience to avoid dereferencing resourceId)
  topicIds: integer("topic_ids").array(),
  skillIds: integer("skill_ids").array(),
  duration: integer("duration"), // in minutes
  difficultyLevel: text("difficulty_level"),
  thumbnailUrl: text("thumbnail_url"), // For activity cards and previews
  viewCount: integer("view_count").default(0), // For analytics
  likesCount: integer("likes_count").default(0), // For social features
  instructorId: integer("instructor_id"), // Reference to instructor user
  isPublic: boolean("is_public").default(true), // Control visibility
  completionCriteria: jsonb("completion_criteria"), // Define when activity is considered complete
  activityData: jsonb("activity_data"), // Arbitrary additional data for specific activity types
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  isExternalContent: boolean("is_external_content").default(false),
  contentSource: text("content_source"),
  relevanceScore: integer("relevance_score").default(0),
  languageCode: text("language_code"),
  readingLevel: text("reading_level"),
});

// Interactive Activities - specialized table for code-based interactive content
export const interactiveActivities = pgTable("interactive_activities", {
  id: serial("id").primaryKey(),
  activityId: integer("activity_id").notNull(), // Reference to the parent learning activity
  title: text("title").notNull(),
  description: text("description"),
  codeContent: text("code_content").notNull(), // The actual code content (HTML, JS, CSS)
  codeType: text("code_type").notNull(), // 'html', 'javascript', 'css', 'react', 'vue', etc.
  framework: text("framework"), // 'vanilla', 'react', 'vue', 'svelte', etc.
  dependencies: jsonb("dependencies"), // Array of external dependencies/libraries
  instructions: text("instructions"), // Instructions for the learner
  completionCriteria: jsonb("completion_criteria"), // Specific completion criteria
  interactionData: jsonb("interaction_data"), // Metadata about expected interactions
  previewUrl: text("preview_url"), // Optional preview/demo URL
  difficulty: text("difficulty"), // beginner, intermediate, advanced
  estimatedDuration: integer("estimated_duration"), // Time in minutes
  tags: text("tags").array(), // Tags for categorization
  isPublic: boolean("is_public").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Learning Plans (PLPs)
export const learningPlans = pgTable("learning_plans", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  courseDefinitionId: integer("course_definition_id"), // Optional association with course definition
  courseOfferingId: integer("course_offering_id"), // Optional association with course offering
  topicIds: integer("topic_ids").array(),
  skillIds: integer("skill_ids").array(),
  skillGraphId: integer("skill_graph_id"), // Reference to associated skill graph
  topicGraphId: integer("topic_graph_id"), // Reference to associated topic graph
  activitySequence: integer("activity_sequence").array(), // Ordered array of activity IDs
  thumbnailUrl: text("thumbnail_url"), // For visual representation
  estimatedDuration: integer("estimated_duration"), // Total duration in minutes
  difficultyLevel: text("difficulty_level"), // Match client difficulty levels
  readingLevel: text("reading_level"), // Grade level or higher education level
  aiAssistanceLevel: text("ai_assistance_level"), // Level of AI assistance
  presentationModality: text("presentation_modality"), // Preferred presentation modality
  completedCount: integer("completed_count").default(0), // Track completions
  viewCount: integer("view_count").default(0), // For analytics
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdById: integer("created_by_id"), // Reference to the user who created this PLP
  isPublic: boolean("is_public").default(false), // Whether this PLP is available to all users
});

// Learning Modules
export const learningModules = pgTable("learning_modules", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  topicIds: integer("topic_ids").array(), // Topics covered in this module
  skillIds: integer("skill_ids").array(), // Skills to be mastered in this module
  skillGraphId: integer("skill_graph_id"), // Reference to associated skill graph
  topicGraphId: integer("topic_graph_id"), // Reference to associated topic graph
  learningPlanIds: integer("learning_plan_ids").array(), // Learning plans included in this module
  assessmentId: integer("assessment_id"), // Optional final assessment
  pathId: integer("path_id"), // Path this module belongs to
  nodeId: text("node_id"), // Node ID in the learning path
  activityIds: integer("activity_ids").array(), // Learning activities included in this module
  difficultyLevel: text("difficulty_level"), // Beginner, intermediate, advanced
  estimatedDuration: integer("estimated_duration"), // Total duration in minutes
  thumbnailUrl: text("thumbnail_url"), // For visual representation
  activityCount: integer("activity_count").default(0), // Cache activity count
  completedCount: integer("completed_count").default(0), // Track completions
  viewCount: integer("view_count").default(0), // For analytics
  createdById: integer("created_by_id"), // User who created the module
  isPublic: boolean("is_public").default(false), // Whether this module is publicly available
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Learning Path Node Types
export const LEARNING_PATH_NODE_TYPES = {
  ACTIVITY: 'activity',
  PLAN: 'plan',
  MODULE: 'module',
  PATH: 'path',
  ASSESSMENT: 'assessment',
} as const;

export type LearningPathNodeType = typeof LEARNING_PATH_NODE_TYPES[keyof typeof LEARNING_PATH_NODE_TYPES];

// Learning Paths (graph structure linking various learning components)
export const learningPaths = pgTable("learning_paths", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  topicIds: integer("topic_ids").array(), // Topics covered in this path
  skillIds: integer("skill_ids").array(), // Skills to be mastered in this path
  skillGraphIds: integer("skill_graph_ids").array(), // References to associated skill graphs
  topicGraphIds: integer("topic_graph_ids").array(), // References to associated topic graphs
  nodes: jsonb("nodes").notNull(), // Array of nodes [{ id: string, type: LearningPathNodeType, entityId: number, label: string }]
  edges: jsonb("edges").notNull(), // Array of edges [{ source: string, target: string, label?: string }]
  startNodeId: text("start_node_id").notNull(), // ID of the starting node
  assessmentId: integer("assessment_id"), // Optional summative assessment
  difficultyLevel: text("difficulty_level"), // Beginner, intermediate, advanced
  estimatedDuration: integer("estimated_duration"), // Total duration in minutes
  thumbnailUrl: text("thumbnail_url"), // For visual representation
  moduleCount: integer("module_count").default(0), // Cache module count
  completedCount: integer("completed_count").default(0), // Track completions
  viewCount: integer("view_count").default(0), // For analytics
  createdById: integer("created_by_id"), // User who created the path
  isPublic: boolean("is_public").default(false), // Whether this path is publicly available
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Learning Outcomes
export const learningOutcomes = pgTable("learning_outcomes", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  skillId: integer("skill_id").notNull(), // Reference to the skill being demonstrated
  measurables: jsonb("measurables").notNull(), // Array of measurable objects
  status: text("status").notNull().default(LEARNING_OUTCOME_STATUS.INITIALIZED),
  pathId: integer("path_id"), // Optional association with learning path
  moduleId: integer("module_id"), // Optional association with learning module
  planId: integer("plan_id"), // Optional association with learning plan
  assessmentId: integer("assessment_id"), // Generated assessment (optional initially)
  rubricIds: integer("rubric_ids").array(), // Associated rubrics (can be generated)
  businessGoalId: integer("business_goal_id"), // Reference to business goal from Goal Path
  createdById: integer("created_by_id"), // User who created this outcome
  approvedById: integer("approved_by_id"), // User who approved this outcome
  approvedAt: timestamp("approved_at"), // When the outcome was approved
  finalizedAt: timestamp("finalized_at"), // When the outcome was finalized
  isPublic: boolean("is_public").default(false), // Whether this outcome is publicly available
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Mastery levels
export const MASTERY_LEVEL = {
  BEGINNER: 'BEGINNER',
  BASIC: 'BASIC',
  INTERMEDIATE: 'INTERMEDIATE',
  ADVANCED: 'ADVANCED',
  EXPERT: 'EXPERT',
} as const;

export type MasteryLevel = typeof MASTERY_LEVEL[keyof typeof MASTERY_LEVEL];

// Learning Action Plan Execution Status
export const LEARNING_ACTION_PLAN_EXECUTION_STATUS = {
  PENDING: 'pending',
  RUNNING: 'running', 
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled'
} as const;

export type LearningActionPlanExecutionStatus = typeof LEARNING_ACTION_PLAN_EXECUTION_STATUS[keyof typeof LEARNING_ACTION_PLAN_EXECUTION_STATUS];

// Learning Action Plans - stores the action plans themselves
export const learningActionPlans = pgTable("learning_action_plans", {
  id: serial("id").primaryKey(),
  planId: text("plan_id").notNull().unique(), // UUID from the action plan
  name: text("name").notNull(),
  description: text("description"),
  userRequest: text("user_request").notNull(), // Original user request that generated this action plan
  pathId: integer("path_id").notNull(), // Target learning path ID
  steps: jsonb("steps").notNull(), // Array of LearningPathActionStep objects
  executionConfig: jsonb("execution_config").notNull(), // Execution configuration
  rollbackConfig: jsonb("rollback_config"), // Optional rollback configuration
  metadata: jsonb("metadata").notNull(), // Plan metadata
  createdById: integer("created_by_id"), // User who created this plan
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Learning Action Plan Executions - tracks execution status and results
// Now unified with processing system using text ID as primary key
export const learningActionPlanExecutions = pgTable("learning_action_plan_executions", {
  id: text("id").primaryKey(), // Now using text ID to unify with processing system
  planId: text("plan_id").notNull(), // References the action plan
  executionId: text("execution_id").notNull().unique(), // Unique execution identifier (same as id)
  userRequest: text("user_request").notNull(), // Original user request that generated this action plan
  status: text("status").notNull().default(LEARNING_ACTION_PLAN_EXECUTION_STATUS.PENDING),
  progress: integer("progress").default(0), // Progress percentage 0-100
  currentStep: text("current_step"), // Current step being executed
  stepResults: jsonb("step_results"), // Array of step execution results
  executionSummary: jsonb("execution_summary"), // Execution summary data
  errors: jsonb("errors"), // Array of errors that occurred
  warnings: jsonb("warnings"), // Array of warnings
  result: jsonb("result"), // Complete execution result
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Schema validation
export const difficultyLevelSchema = z.enum([
  DIFFICULTY_LEVELS.BEGINNER,
  DIFFICULTY_LEVELS.INTERMEDIATE, 
  DIFFICULTY_LEVELS.ADVANCED,
  DIFFICULTY_LEVELS.EXPERT
]);

export const readingLevelSchema = z.enum([
  READING_LEVELS.GRADE_1,
  READING_LEVELS.GRADE_2,
  READING_LEVELS.GRADE_3,
  READING_LEVELS.GRADE_4,
  READING_LEVELS.GRADE_5,
  READING_LEVELS.GRADE_6,
  READING_LEVELS.GRADE_7,
  READING_LEVELS.GRADE_8,
  READING_LEVELS.GRADE_9,
  READING_LEVELS.GRADE_10,
  READING_LEVELS.GRADE_11,
  READING_LEVELS.GRADE_12,
  READING_LEVELS.UNDERGRAD,
  READING_LEVELS.GRADUATE,
  READING_LEVELS.PHD
]);

export const aiAssistanceLevelSchema = z.enum([
  AI_ASSISTANCE_LEVELS.MINIMAL,
  AI_ASSISTANCE_LEVELS.MODERATE,
  AI_ASSISTANCE_LEVELS.EXTENSIVE
]);

export const presentationModalitySchema = z.enum([
  PRESENTATION_MODALITIES.TEXT,
  PRESENTATION_MODALITIES.VIDEO,
  PRESENTATION_MODALITIES.AUDIO,
  PRESENTATION_MODALITIES.INTERACTIVE,
  PRESENTATION_MODALITIES.MIXED
]);

export const learningOutcomeStatusSchema = z.enum([
  LEARNING_OUTCOME_STATUS.INITIALIZED,
  LEARNING_OUTCOME_STATUS.APPROVED,
  LEARNING_OUTCOME_STATUS.GENERATING,
  LEARNING_OUTCOME_STATUS.FINALIZED
]);

// Mastery Level Schema
export const masteryLevelSchema = z.enum([
  MASTERY_LEVEL.BEGINNER,
  MASTERY_LEVEL.BASIC,
  MASTERY_LEVEL.INTERMEDIATE,
  MASTERY_LEVEL.ADVANCED,
  MASTERY_LEVEL.EXPERT
]);

// Measurable Schema - structure for individual measurable items
export const measurableSchema = z.object({
  id: z.string(), // Unique identifier for the measurable
  description: z.string(), // Description of the action to be performed
  assessmentId: z.number().optional(), // Associated assessment (optional initially)
  rubricId: z.number().optional(), // Associated rubric (optional initially)
  requiredMastery: masteryLevelSchema, // Required mastery level (BEGINNER, BASIC, INTERMEDIATE, ADVANCED, EXPERT)
  weight: z.number().min(0).max(1).default(1), // Weight in overall outcome score
});

export type Measurable = z.infer<typeof measurableSchema>;



// Personalized Learning Plan Parameters Schema
export const personalizedLearningParamsSchema = z.object({
  skillIds: z.array(z.number()).optional(),
  topicIds: z.array(z.number()).optional(),
  userId: z.number(),
  desiredDuration: z.number().optional(), // in minutes
  difficultyLevel: difficultyLevelSchema.optional(),
  readingLevel: readingLevelSchema.optional(),
  aiAssistanceLevel: aiAssistanceLevelSchema.optional(),
  presentationModality: presentationModalitySchema.optional(),
  title: z.string().optional(),
  description: z.string().optional()
});

export type PersonalizedLearningParams = z.infer<typeof personalizedLearningParamsSchema>;

// Learning Action Plan Execution Status Schema
export const learningActionPlanExecutionStatusSchema = z.enum([
  LEARNING_ACTION_PLAN_EXECUTION_STATUS.PENDING,
  LEARNING_ACTION_PLAN_EXECUTION_STATUS.RUNNING,
  LEARNING_ACTION_PLAN_EXECUTION_STATUS.COMPLETED,
  LEARNING_ACTION_PLAN_EXECUTION_STATUS.FAILED,
  LEARNING_ACTION_PLAN_EXECUTION_STATUS.CANCELLED
]);

// Insert schemas
export const insertLearningActivitySchema = createInsertSchema(learningActivities).omit({ id: true, createdAt: true, updatedAt: true });
export const insertInteractiveActivitySchema = createInsertSchema(interactiveActivities).omit({ id: true, createdAt: true, updatedAt: true });
export const insertLearningPlanSchema = createInsertSchema(learningPlans).omit({ id: true, createdAt: true, updatedAt: true });
export const insertLearningModuleSchema = createInsertSchema(learningModules).omit({ id: true, createdAt: true, updatedAt: true });
export const insertLearningPathSchema = createInsertSchema(learningPaths).omit({ id: true, createdAt: true, updatedAt: true });
export const insertLearningOutcomeSchema = createInsertSchema(learningOutcomes).omit({ id: true, createdAt: true, updatedAt: true, approvedAt: true, finalizedAt: true });
export const insertLearningActionPlanSchema = createInsertSchema(learningActionPlans).omit({ id: true, createdAt: true, updatedAt: true });
export const insertLearningActionPlanExecutionSchema = createInsertSchema(learningActionPlanExecutions).omit({ createdAt: true, updatedAt: true });

// Enhanced validation schemas for learning outcomes with proper measurables validation
// This ensures that:
// - requiredMastery field is validated against MASTERY_LEVEL enum (BEGINNER, BASIC, INTERMEDIATE, ADVANCED, EXPERT)
// - weight field is between 0 and 1
// - At least one measurable is required for creation
// - All required fields in measurables are present (id, description, requiredMastery)
export const createLearningOutcomeSchema = insertLearningOutcomeSchema.extend({
  measurables: z.array(measurableSchema).min(1, 'At least one measurable is required')
});

export const updateLearningOutcomeSchema = insertLearningOutcomeSchema.partial().extend({
  measurables: z.array(measurableSchema).min(1, 'At least one measurable is required').optional()
});

// Types
export type LearningActivity = typeof learningActivities.$inferSelect;
export type InsertLearningActivity = z.infer<typeof insertLearningActivitySchema>;
export type InteractiveActivity = typeof interactiveActivities.$inferSelect;
export type InsertInteractiveActivity = z.infer<typeof insertInteractiveActivitySchema>;
export type LearningPlan = typeof learningPlans.$inferSelect;
export type InsertLearningPlan = z.infer<typeof insertLearningPlanSchema>;
export type LearningModule = typeof learningModules.$inferSelect;
export type InsertLearningModule = z.infer<typeof insertLearningModuleSchema>;
export type LearningPath = typeof learningPaths.$inferSelect;
export type InsertLearningPath = z.infer<typeof insertLearningPathSchema>;
export type LearningOutcome = typeof learningOutcomes.$inferSelect;
export type InsertLearningOutcome = z.infer<typeof insertLearningOutcomeSchema>;
export type LearningActionPlan = typeof learningActionPlans.$inferSelect;
export type InsertLearningActionPlan = z.infer<typeof insertLearningActionPlanSchema>;
export type LearningActionPlanExecution = typeof learningActionPlanExecutions.$inferSelect;
export type InsertLearningActionPlanExecution = z.infer<typeof insertLearningActionPlanExecutionSchema>;

// Skillshop Generation History
export const skillshopGenerationHistory = pgTable("skillshop_generation_history", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  query: text("query").notNull(), // Original user query
  userId: integer("user_id"), // User who created this generation (optional for now)
  planTitle: text("plan_title"), // Title from the generated plan
  planDescription: text("plan_description"), // Description from the generated plan
  difficultyLevel: text("difficulty_level"),
  readingLevel: text("reading_level"),
  language: text("language"),
  skillIds: integer("skill_ids").array(),
  skillCount: integer("skill_count").default(0),
  topicIds: integer("topic_ids").array(),
  topicCount: integer("topic_count").default(0),
  learningActivityIds: integer("learning_activity_ids").array(),
  activityCount: integer("activity_count").default(0),
  learningPlanId: integer("learning_plan_id"),
  learningModuleId: integer("learning_module_id"),
  learningPathId: integer("learning_path_id"),
  topicGraphId: integer("topic_graph_id"), // ID of the knowledge tree for this generation
  createdAt: timestamp("created_at").defaultNow(),
  isSuccessful: boolean("is_successful").default(true),
  errorMessage: text("error_message"),
});

// Insert schemas
export const insertSkillshopGenerationHistorySchema = createInsertSchema(skillshopGenerationHistory).omit({ id: true, createdAt: true });

// Types
export type SkillshopGenerationHistory = typeof skillshopGenerationHistory.$inferSelect;
export type InsertSkillshopGenerationHistory = z.infer<typeof insertSkillshopGenerationHistorySchema>; 